import { createStyles, Paper, Typography } from '@material-ui/core';
import React from 'react';
import update from 'react-addons-update';

import { updatePassword } from '~/src/dash/api/console-api';
import { IObject } from '~/src/dash/api/resource-transforms';
import { AsyncButtonOldV2 } from '~/src/dash/components/button';
import { FormItemTypeText, IFormData, IFormItem, RenderForm } from '~/src/dash/components/form';
import { IModelBase } from '~/src/dash/components/model';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ct, Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { routeName } from '~/src/dash/utils/routes';
import { hasErrors, IErrors, transformErrors, validatorFactory } from '~/src/dash/utils/validator';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IModelBase {
  // password panel
  CurrentPassword: string;
  NewPassword: string;
  ConfirmPassword: string;

  errors: IErrors;
  snackbarMessage: string;
  snackbarError: boolean;
}

const passwordForm: IFormItem[] = [
  {
    id: 'CurrentPassword',
    label: 'Current Password',
    type: 'password',
    required: true,
    render: FormItemTypeText,
  },
  {
    id: 'NewPassword',
    label: 'New Password',
    type: 'password',
    required: true,
    render: FormItemTypeText,
  },
  {
    id: 'ConfirmPassword',
    label: 'Confirm Password',
    type: 'password',
    required: true,
    render: FormItemTypeText,
  },
];

const validate = validatorFactory({
  NewPassword(_val: any, _key: string, _obj: IObject): string {
    if (!!_val && _val.length < 8) {
      return 'Your password needs to be at least 8 characters long.';
    } else if (_val === _obj.CurrentPassword) {
      return 'please choose a password different from your current one';
    }
    return '';
  },
  ConfirmPassword(_val: any, _key: string, _obj: IObject): string {
    if (!!_val && _val.length < 8) {
      return 'Your password needs to be at least 8 characters long.';
    } else if (_val !== _obj.NewPassword) {
      return 'Your passwords did not match';
    }
    return '';
  },
});

export const AccountSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) =>
      createStyles({
        paper: {
          ...theme.mixins.gutters(),
          paddingTop: theme.spacing.unit * 2,
          paddingBottom: theme.spacing.unit * 2,
          marginTop: theme.spacing.unit * 4,
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: '600px',
        },
        modelSelectTextField: {
          margin: 0,
          width: '100%',
        },
      }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      CurrentPassword: '',
      NewPassword: '',
      ConfirmPassword: '',
      errors: {} as IErrors,
      snackbarMessage: '',
      snackbarError: false,
    } as IState;

    validateForm = (): boolean => {
      const errors = validate(this.state);
      if (hasErrors(errors)) {
        this.setState({
          errors,
        });
        return false;
      }
      this.setState({
        errors: {} as IErrors,
      });
      return true;
    };

    onUpdatePassword = (data: IFormData) => {
      this.setState({
        CurrentPassword: data.CurrentPassword,
        NewPassword: data.NewPassword,
        ConfirmPassword: data.ConfirmPassword,
      });
    };

    updatePassword = (done: (err?: Error) => void) => {
      if (!this.validateForm()) {
        this.setState({
          snackbarMessage: 'Updating password failed. Please fix the errors and try again later.',
          snackbarError: true,
        });
        done();
        return;
      }
      updatePassword({
        query: {
          pass_data: {
            Current: this.state.CurrentPassword,
            New: this.state.NewPassword,
          },
        },
      })
        .then((res: any) => {
          if (res.success) {
            this.setState(
              {
                CurrentPassword: '',
                NewPassword: '',
                ConfirmPassword: '',
                snackbarMessage: 'Successfully Updated your password.',
                snackbarError: false,
              },
              done,
            );
          } else {
            this.setState(
              {
                snackbarMessage:
                  'Updating password failed. Please fix the errors and try again later.',
                snackbarError: true,
              },
              () => {
                if (res.currentnotvalid) {
                  this.setState(
                    {
                      errors: update(this.state.errors, {
                        $merge: {
                          CurrentPassword: 'Current password is not valid',
                        },
                      }),
                    },
                    done,
                  );
                }
              },
            );
          }
        })
        .catch(done);
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleAsyncButtonClick = () => {
      return (done: () => void) => {
        this.updatePassword(() => {
          done();
        });
      };
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              <Typography variant='h6' gutterBottom>
                Update Password
              </Typography>
              <p>
                {ct('Email')}: {user.user.Username}
              </p>
              <RenderForm
                schema={passwordForm}
                data={{
                  CurrentPassword: this.state.CurrentPassword,
                  NewPassword: this.state.NewPassword,
                  ConfirmPassword: this.state.ConfirmPassword,
                }}
                errors={transformErrors(this.state.errors || {})}
                onUpdate={this.onUpdatePassword}
              />
              <div style={{ marginTop: '2rem' }}>
                <AsyncButtonOldV2 text='Save' onClick={this.handleAsyncButtonClick()} />
              </div>
            </Paper>
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
              successBar={true}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
